import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { de, type TranslationKey } from "./de.js";
import { en } from "./en.js";

// ── Supported locales ──────────────────────────────────────
export type Locale = "de" | "en";

const TRANSLATIONS: Record<Locale, Record<string, string>> = { de, en };

const STORAGE_KEY = "solitaire-locale";

function getInitialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "de" || stored === "en") return stored;
  } catch { /* SSR / blocked storage */ }
  return "de";
}

// ── Interpolation ──────────────────────────────────────────
function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    const val = params[key];
    return val !== undefined ? String(val) : `{{${key}}}`;
  });
}

// ── Translation function type ──────────────────────────────
export type TFunction = (key: TranslationKey, params?: Record<string, string | number>) => string;

// ── React Context ──────────────────────────────────────────
interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: TFunction;
}

const I18nContext = createContext<I18nContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────
export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
  }, []);

  const t: TFunction = useCallback(
    (key, params) => {
      const str = TRANSLATIONS[locale]?.[key] ?? TRANSLATIONS.de[key] ?? key;
      return interpolate(str, params);
    },
    [locale],
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────
export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

// Re-export für bequemen Zugriff
export type { TranslationKey };
